import { useRef, useState, useEffect, useCallback } from "react";
import { Volume2, VolumeX, Play, ChevronLeft, ChevronRight } from "lucide-react";

const VIDEOS = [
  {
    mp4: "/videos/showcase.mp4",
    webm: "/videos/showcase.webm",
    poster: "/videos/showcase-poster.jpg",
    caption: "Loading up · Louisville",
  },
  {
    mp4: "/videos/showcase-2.mp4",
    webm: "/videos/showcase-2.webm",
    poster: "/videos/showcase-2-poster.jpg",
    caption: "On the job · Kentucky crew",
  },
  {
    mp4: "/videos/showcase-3.mp4",
    webm: "/videos/showcase-3.webm",
    poster: "/videos/showcase-3-poster.jpg",
    caption: "Wrapped & delivered",
  },
];

export default function VideoShowcase() {
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  const touchStartX = useRef(null);

  const goTo = useCallback((next) => {
    const n = (next + VIDEOS.length) % VIDEOS.length;
    setIdx(n);
  }, []);
  const prev = () => goTo(idx - 1);
  const next = () => goTo(idx + 1);

  // Pause any background <audio> when unmuting
  const toggleMute = () => {
    const el = videoRef.current;
    if (!el) return;
    const nextMuted = !muted;
    el.muted = nextMuted;
    setMuted(nextMuted);
    if (!nextMuted) {
      document.querySelectorAll("audio").forEach((a) => {
        try {
          a.pause();
        } catch (_) {}
      });
    }
  };

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => {});
    else el.pause();
  };

  // Auto-advance when a video ends
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const onEnded = () => goTo(idx + 1);
    el.addEventListener("ended", onEnded);
    return () => el.removeEventListener("ended", onEnded);
  }, [idx, goTo]);

  // Swipe handlers
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  const current = VIDEOS[idx];

  return (
    <section
      id="see-us-in-action"
      className="bg-black py-20 lg:py-28 border-t border-[#1f1f1f]"
      data-testid="video-showcase-section"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-center">
          {/* Copy */}
          <div className="lg:col-span-2">
            <div className="overline text-[#5BB239]">See Us In Action</div>
            <h2 className="mt-3 font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
              Real trucks.
              <br />
              Real crews.
              <br />
              <span className="text-[#5BB239]">Real moves.</span>
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-md">
              Not stock footage. Our crew, our trucks, straight from the job.
              Swipe through to see how we roll.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#book"
                className="inline-flex items-center gap-3 bg-[#5BB239] text-black px-6 py-3.5 font-bold uppercase tracking-wider text-sm hover:bg-[#499828] hover:text-white transition-colors"
                data-testid="video-book-btn"
              >
                Book My Move
              </a>
              <a
                href="#quote"
                className="inline-flex items-center gap-3 border border-white/25 text-white px-6 py-3.5 font-bold uppercase tracking-wider text-sm hover:bg-white/10 transition-colors"
                data-testid="video-quote-btn"
              >
                Get A Quote
              </a>
            </div>
          </div>

          {/* Reel */}
          <div className="lg:col-span-3 flex justify-center lg:justify-end">
            <div className="relative w-full max-w-[380px] lg:max-w-[420px]">
              {/* Frame */}
              <div
                className="relative border border-white/10 shadow-2xl bg-[#151816] overflow-hidden"
                style={{ aspectRatio: "9 / 16" }}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                data-testid="video-reel-frame"
              >
                <video
                  key={current.mp4}
                  ref={videoRef}
                  poster={current.poster}
                  autoPlay
                  muted={muted}
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 w-full h-full object-cover cursor-pointer"
                  onClick={togglePlay}
                  data-testid="showcase-video"
                >
                  <source src={current.webm} type="video/webm" />
                  <source src={current.mp4} type="video/mp4" />
                </video>

                {/* Brand tag */}
                <div className="absolute top-4 left-4 bg-[#5BB239] text-black font-mono-label text-[9px] px-2.5 py-1 tracking-widest z-10">
                  ThemBoys · REEL {String(idx + 1).padStart(2, "0")}
                </div>

                {/* Caption pill */}
                <div className="absolute bottom-16 left-4 right-4 z-10">
                  <div className="inline-block bg-black/70 backdrop-blur border border-white/15 text-white px-3 py-1.5 font-mono-label text-[10px] tracking-widest">
                    {current.caption}
                  </div>
                </div>

                {/* Controls */}
                <div className="absolute bottom-4 right-4 flex items-center gap-2 z-10">
                  <button
                    type="button"
                    onClick={togglePlay}
                    aria-label="Play/Pause"
                    className="bg-black/70 backdrop-blur border border-white/20 text-white p-2.5 hover:bg-black transition-colors"
                    data-testid="video-play-btn"
                  >
                    <Play className="w-4 h-4" fill="currentColor" />
                  </button>
                  <button
                    type="button"
                    onClick={toggleMute}
                    aria-label={muted ? "Unmute" : "Mute"}
                    className="bg-black/70 backdrop-blur border border-white/20 text-white p-2.5 hover:bg-black transition-colors"
                    data-testid="video-mute-btn"
                  >
                    {muted ? (
                      <VolumeX className="w-4 h-4" />
                    ) : (
                      <Volume2 className="w-4 h-4" />
                    )}
                  </button>
                </div>

                {/* Prev / Next arrows */}
                <button
                  type="button"
                  onClick={prev}
                  aria-label="Previous video"
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black backdrop-blur border border-white/15 text-white p-2 z-10 opacity-0 hover:opacity-100 focus:opacity-100 group-hover:opacity-100 transition-opacity md:opacity-70"
                  data-testid="reel-prev-btn"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={next}
                  aria-label="Next video"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black backdrop-blur border border-white/15 text-white p-2 z-10 opacity-0 hover:opacity-100 focus:opacity-100 group-hover:opacity-100 transition-opacity md:opacity-70"
                  data-testid="reel-next-btn"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Dots */}
              <div className="mt-4 flex items-center justify-center gap-2">
                {VIDEOS.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => goTo(i)}
                    aria-label={`Go to reel ${i + 1}`}
                    className={`h-1.5 transition-all ${
                      i === idx ? "w-8 bg-[#5BB239]" : "w-4 bg-white/25 hover:bg-white/50"
                    }`}
                    data-testid={`reel-dot-${i}`}
                  />
                ))}
              </div>
              <div className="mt-2 text-center font-mono-label text-[9px] text-white/40 tracking-widest">
                Swipe · Tap dots · {idx + 1} of {VIDEOS.length}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
